import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  EventDiscount,
  EventPricingMatrix,
  EventTypeWithPricingMatrices
} from '../../../../../../../models/event-type';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { MyAccount } from '../../../../../../../models/my-account';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { Association } from '../../../../../../../models/association';
import { AssociationService } from '../../../../../../../services/association/association.service';
import { Membership } from '../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../services/membership/membership.service';
import { ComponentHasUnsavedChanges } from '../../../../../../../unsaved-changes-guard.service';
import { EventDiscountType, EventTypeStatus } from '../../../../../../../models/event-type-enums';
import { MembershipStatus } from '../../../../../../../models/membership-enums';


@Component({
  selector: 'app-event-discount-detail',
  templateUrl: './event-discount-detail.component.html',
  styleUrls: ['./event-discount-detail.component.css']
})
export class EventDiscountDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewDiscount: boolean;
  public discount: EventDiscount = {
    id: null,
    url: null,
    is_automatic: false,
    code: '',
    name: '',
    discount_type: EventDiscountType.currency,
    amount: null,
    is_per_participant: true,
    applies_to_add_ons: false,
    start_date: null,
    end_date: null,
    pricing_matrix_ids: [],
    pricing_matrix_details: [],
    requires_association: false,
    requires_all_associations: false,
    association_ids: [],
    association_names: [],
    requires_membership: false,
    required_membership_ids: [],
    required_membership_names: [],
    max_number_of_uses_per_customer: null,
    required_no_check_in_period_days: null,
    min_check_in_count: null,
    max_check_in_count: null,
    min_number_of_days_without_membership: null,
    min_age: null,
    max_age: null,
    min_days_until_event_start: null,
    max_days_until_event_start: null,
    created_at: null,
    updated_at: null
  };
  public eventTypesWithPricingMatrices: EventTypeWithPricingMatrices[];
  public associations: Association[];
  public memberships: Membership[];

  // Used to ensure retired pricing matrices are included as options if they are already on the discount.
  // Otherwise, retired pricing matrices are not shown.
  public initialPricingMatrixIds: number[] = [];

  public errorMessage = '';
  public errors: any = {};
  public discountNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public loadingExistingDiscount = false;
  public loadingEventTypesWithPricingMatrices = true;
  public loadingAssociations = true;
  public loadingMemberships = true;
  public loadingSubmit = false;
  public loadingDelete = false;

  public myAccount: MyAccount;

  public EventTypeStatus = EventTypeStatus;
  public EventDiscountType = EventDiscountType;
  public MembershipStatus = MembershipStatus;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private eventTypeService: EventTypeService,
    private associationService: AssociationService,
    private membershipService: MembershipService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewDiscount = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the discount to edit, if an ID was provided.
    if (!this.isNewDiscount) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.discountNotFound = true;
        this.loadingExistingDiscount = false;
        return;
      }

      this.loadDiscount(idInt);
    }

    this.loadEventTypesWithPricingMatrices();
    this.loadAssociations();
    this.loadMemberships();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public loadDiscount(id: number) {
    this.loadingExistingDiscount = true;

    this.eventTypeService
      .getEventDiscount(id)
      .then(discount => {
        this.discount = discount;
        this.initialPricingMatrixIds = [...this.discount.pricing_matrix_ids];
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingDiscount = false; });
  }

  public loadEventTypesWithPricingMatrices() {
    this.loadingEventTypesWithPricingMatrices = true;

    this.eventTypeService
      .getAllEventPricingMatrices()
      .then(result => {
        this.eventTypesWithPricingMatrices = result;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypesWithPricingMatrices = false; });
  }

  public loadAssociations() {
    this.loadingAssociations = true;

    this.associationService
      .getAssociations(1, 3000, {})
      .then(result => {
        this.associations = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociations = false; });
  }

  public loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {include_rolling: true}
      )
      .then(result => {
        this.memberships = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMemberships = false; });
  }

  public handleSubmit() {
    if (this.loadingSubmit) { return; }

    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<EventDiscount>;
    if (this.isNewDiscount) {
      savePromise = this.eventTypeService.addEventDiscount(this.discount);
    } else {
      savePromise = this.eventTypeService.updateEventDiscount(this.discount);
    }

    savePromise
      .then(savedDiscount => {
        this.discount = savedDiscount;

        this.unsavedChanges = false;

        let successMessage: string;
        let discountName = this.discount.name;
        if (!discountName) {
          discountName = this.discount.code;
        }
        if (this.isNewDiscount) {
          successMessage = discountName + ' created successfully.';
        } else {
          successMessage = discountName + ' updated successfully.';
        }

        this.router.navigate(
          ['/events/setup/discounts'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewDiscount) {
            this.errorMessage = 'You do not have permission to add discounts.';
          } else {
            this.errorMessage = 'You do not have permission to modify this discount.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Discount';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete this discount? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.eventTypeService
        .deleteEventDiscount(this.discount)
        .then(() => {
          this.unsavedChanges = false;

          let discountName = this.discount.name;
          if (!discountName) {
            discountName = this.discount.code;
          }

          this.router.navigate(
            ['/events/setup/discounts'],
            {
              state: {
                feedback: {
                  text: discountName + ' deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this discount.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  public getFilteredEventTypesWithPricingMatrices(): EventTypeWithPricingMatrices[] {
    return this.eventTypesWithPricingMatrices.filter(et => {
      if ([EventTypeStatus.draft, EventTypeStatus.active].includes(et.status)) {
        return true;
      }

      // Allow matrices belonging to retired event types if they are already assigned.
      for (const pm of et.pricing_matrices) {
        if (this.initialPricingMatrixIds.includes(pm.id)) { return true; }
      }

      return false;
    })
  }

  public pricingMatrixSelected(pricingMatrix: EventPricingMatrix) {
    return this.discount.pricing_matrix_ids.includes(pricingMatrix.id);
  }

  public togglePricingMatrix(pricingMatrix: EventPricingMatrix) {
    if (this.pricingMatrixSelected(pricingMatrix)) {
      this.discount.pricing_matrix_ids.splice(this.discount.pricing_matrix_ids.indexOf(pricingMatrix.id), 1);
    } else {
      this.discount.pricing_matrix_ids.push(pricingMatrix.id);
    }
  }

  public allPricingMatricesAreSelected() {
    for (const et of this.getFilteredEventTypesWithPricingMatrices()) {
      for (const pm of et.pricing_matrices) {
        if (!this.discount.pricing_matrix_ids.includes(pm.id)) {
          return false;
        }
      }
    }

    return true;
  }

  public setNoPricingMatricesSelected() {
    this.discount.pricing_matrix_ids = [];
  }

  public setAllPricingMatricesSelected() {
    this.discount.pricing_matrix_ids = [];
    for (const et of this.getFilteredEventTypesWithPricingMatrices()) {
      for (const pm of et.pricing_matrices) {
        this.discount.pricing_matrix_ids.push(pm.id);
      }
    }
  }

  public associationSelected(association: Association) {
    return this.discount.association_ids.includes(association.id);
  }

  public toggleAssociation(association: Association) {
    if (this.associationSelected(association)) {
      this.discount.association_ids.splice(this.discount.association_ids.indexOf(association.id), 1);
    } else {
      this.discount.association_ids.push(association.id);
    }
  }

  public allAssociationsAreSelected() {
    for (const association of this.associations) {
      if (!this.discount.association_ids.includes(association.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoAssociationsSelected() {
    this.discount.association_ids = [];
  }

  public setAllAssociationsSelected() {
    this.discount.association_ids = this.associations.map(ep => ep.id);
  }

  public requiredMembershipSelected(membership: Membership) {
    return this.discount.required_membership_ids.includes(membership.id);
  }

  public toggleRequiredMembership(membership: Membership) {
    if (this.requiredMembershipSelected(membership)) {
      this.discount.required_membership_ids.splice(
        this.discount.required_membership_ids.indexOf(membership.id),
        1
      );
    } else {
      this.discount.required_membership_ids.push(membership.id);
    }
  }

  public allRequiredMembershipsAreSelected() {
    for (const membership of this.memberships) {
      if (!this.discount.required_membership_ids.includes(membership.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoRequiredMembershipsSelected() {
    this.discount.required_membership_ids = [];
  }

  public setAllRequiredMembershipsSelected() {
    this.discount.required_membership_ids = this.memberships.map(m => m.id);
  }

  public isLoading() {
    return (
      this.loadingExistingDiscount ||
      this.loadingEventTypesWithPricingMatrices ||
      this.loadingAssociations ||
      this.loadingSubmit ||
      this.loadingDelete
    )
  }
}
